import { defineComponent } from 'vue'
import { StackingOrder } from '../../constants/index'
import { BeerCls } from '../../../core/ds/index.js'
import Toast from './Toast.vue'
import { BeerStyleObj } from '../../../core'

let idCounter = 0
const hasCustomId = (settings: any) => Object.hasOwnProperty.call(settings, 'id')

const wrapperClass = BeerCls({
  maxWidth: 560,
  margin: '0 auto',
  top: 60,
  left: 0,
  right: 0,
  position: 'fixed',
  zIndex: StackingOrder.TOASTER,
  pointerEvents: 'none',
})

export interface ToastSettings {
  titleBs?: BeerStyleObj
  bs?: BeerStyleObj
  duration?: number
  id?: string
}

export interface ToastType {
  settings: ToastSettings
  id: string
  title: string
  isShown?: boolean
  close: () => void
}

export default defineComponent({
  name: 'ToastManager',
  provide() {
    const context = {
      theme: this.theme,
      getTheme: () => this.theme,
    }
    return context
  },
  props: {
    theme: {
      type: Object,
      required: true,
    },
  },
  data() {
    const toasts: ToastType[] = []
    return {
      toasts,
    }
  },
  methods: {
    notify(title: string, settings: ToastSettings) {
      // If there's a custom toast ID passed, close existing toasts with the same custom ID
      if (hasCustomId(settings)) {
        this.toasts.forEach(toast => {
          // Since unique ID is still appended to a custom ID,
          // skip the unique ID and check only prefix
          if (String(toast.id).startsWith(settings.id as string)) {
            this.closeToast(toast.id)
          }
        })
      }

      const instance = this.createToastInstance(title, settings)

      this.toasts = [instance, ...this.toasts]
      return instance
    },

    /**
     * This will set isShown on the Toast which will close the toast.
     * It won't remove the toast until onExited triggers onRemove.
     */
    closeToast(id: string) {
      this.toasts = this.toasts.map(toast => {
        if (id === toast.id) {
          return {
            ...toast,
            isShown: false,
          }
        }
        return toast
      })
    },
    removeToast(id: string) {
      this.toasts = this.toasts.filter(toast => toast.id !== id)
    },
    createToastInstance(title: string, settings: any): ToastType {
      idCounter += 1
      const uniqueId = idCounter
      const id = hasCustomId(settings)
        ? `${settings.id}-${uniqueId}`
        : `${uniqueId}`

      return {
        settings,
        id,
        title,
        close: () => this.closeToast(id),
      }
    },
  },
  render() {
    return (
      <div
        class={wrapperClass}
        data-beer-toaster-container
      >
        {this.toasts.map(toast => (
          <Toast
            key={toast.id}
            title={toast.title}
            isShown={toast.isShown}
            {...{
              onRemove: () => this.removeToast(toast.id),
              ...toast.settings,
            }}
          />
        ))}
      </div>
    )
  },
})
